import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import Flatpickr from "react-flatpickr";
import moment from "moment";

import {
  ColumnHeader,
  DateColumn,
  TextColumn,
  AppTable,
  TableSpinner,
} from "../../components";
import {
  useGetLogGroupsQuery,
  useGetLogStatsQuery,
  useGetLogsQuery,
} from "../../store/api/logApi";
import LogDetailModal from "./LogDetailModal";
import { BreadCrumb } from "../../components/Common";
import { PaginateQuery } from "../../models";
import { LogGroupBy, LogGroupDto, LogsDto } from "../../interfaces";
import { LogAdvancedFilters, LogFilterState } from "./LogAdvancedFilters";
import { RequireSuperAdmin } from "../../routes/AuthProtected";
import { RoleEnum } from "../../enums";
import { useSelector } from "react-redux";
import { AccountState } from "../../store/slices/accountSlice";
import { LogLevelBadge } from "./LogLevelBadge";
import { LogStatsWidgets } from "./LogStatsWidgets";
import { LogServiceHealth } from "./LogServiceHealth";
import { LogGroupsPanel } from "./LogGroupsPanel";
// @ts-ignore
import "./LogsPage.scss";

type LogsTab = "overview" | "logs" | "issues";

const LOG_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSS";

function formatLogDateTime(value: moment.Moment): string {
  return value.format(LOG_DATE_TIME_FORMAT);
}

function startOfDay(day: moment.Moment): string {
  return formatLogDateTime(
    day.clone().hour(0).minute(0).second(0).millisecond(0),
  );
}

function endOfDay(day: moment.Moment): string {
  return formatLogDateTime(
    day.clone().hour(23).minute(59).second(59).millisecond(0),
  );
}

function todayDateTimeRange(): [string, string] {
  const today = moment();
  return [startOfDay(today), endOfDay(today)];
}

function toLogDateTime(value: string, bound: "start" | "end"): string {
  const parsed = moment(value);
  if (!parsed.isValid()) {
    const [from, to] = todayDateTimeRange();
    return bound === "start" ? from : to;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return bound === "start" ? startOfDay(parsed) : endOfDay(parsed);
  }

  return formatLogDateTime(parsed.millisecond(0));
}

const DEFAULT_FILTERS: LogFilterState = {
  level: "",
  statusCode: "",
  serviceName: "",
  host: "",
  method: "",
  errorCode: "",
  errorName: "",
  userId: "",
  ip: "",
  message: "",
  ipMissing: false,
  httpErrorsOnly: false,
};

function filtersToQueryFilter(
  filters: LogFilterState,
  from: string,
  to: string,
) {
  return {
    from,
    to,
    level: filters.level,
    statusCode: filters.statusCode,
    serviceName: filters.serviceName,
    host: filters.host,
    method: filters.method,
    errorCode: filters.errorCode,
    errorName: filters.errorName,
    userId: filters.userId,
    ip: filters.ip,
    message: filters.message,
    ipMissing: filters.ipMissing ? "true" : "",
    httpErrorsOnly: filters.httpErrorsOnly ? "true" : "",
  };
}

function readFiltersFromQuery(
  params: URLSearchParams,
): Partial<LogFilterState> {
  return {
    level: params.get("filter[level]") || "",
    statusCode: params.get("filter[statusCode]") || "",
    serviceName: params.get("filter[serviceName]") || "",
    host: params.get("filter[host]") || "",
    method: params.get("filter[method]") || "",
    errorCode: params.get("filter[errorCode]") || "",
    errorName: params.get("filter[errorName]") || "",
    userId: params.get("filter[userId]") || "",
    ip: params.get("filter[ip]") || "",
    message: params.get("filter[message]") || "",
    ipMissing: params.get("filter[ipMissing]") === "true",
    httpErrorsOnly: params.get("filter[httpErrorsOnly]") === "true",
  };
}

function readGroupBy(params: URLSearchParams): LogGroupBy {
  const value = params.get("groupBy");
  if (value === "message" || value === "ip") {
    return value;
  }
  return "signature";
}

export const LogsPage: React.FC = () => {
  return (
    <RequireSuperAdmin>
      <LogsPageContent />
    </RequireSuperAdmin>
  );
};

const LogsPageContent: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account?.userProfile,
  );
  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;

  const [defaultRange] = useState<[string, string]>(() => todayDateTimeRange());
  const [groupBy, setGroupBy] = useState<LogGroupBy>(() =>
    readGroupBy(new URLSearchParams(window.location.search)),
  );
  const [activeTab, setActiveTab] = useState<LogsTab>("overview");
  const [filters, setFilters] = useState<LogFilterState>(DEFAULT_FILTERS);
  const [dateValue, setDateValue] = useState<[string, string]>(defaultRange);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [selectedLog, setSelectedLog] = useState<LogsDto | null | undefined>();
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(
    new PaginateQuery(
      25,
      "timestamp:DESC",
      filtersToQueryFilter(DEFAULT_FILTERS, defaultRange[0], defaultRange[1]),
    ),
  );

  const filterQuery = useMemo(
    () => ({
      filter: paginateQuery.filter,
      search: paginateQuery.search,
    }),
    [paginateQuery.filter, paginateQuery.search],
  );

  const groupsQuery = useMemo(() => {
    const query = new PaginateQuery(50, "count:DESC", {
      ...(paginateQuery.filter || {}),
      groupBy,
    });
    query.page = 1;
    query.search = paginateQuery.search || "";
    return query;
  }, [paginateQuery.filter, paginateQuery.search, groupBy]);

  const {
    data: response,
    isLoading: loading,
    refetch,
  } = useGetLogsQuery(paginateQuery, { skip: !isSuperAdmin });

  const { data: stats, isLoading: statsLoading } = useGetLogStatsQuery(
    filterQuery,
    { skip: !isSuperAdmin },
  );

  const { data: groups, isLoading: groupsLoading } = useGetLogGroupsQuery(
    groupsQuery,
    { skip: !isSuperAdmin },
  );

  document.title = `${t("documentTitle", { pageName: "logs" })}`;

  const updatePaginateQuery = (
    updater: (current: PaginateQuery) => PaginateQuery,
  ) => {
    setPaginateQuery((current) => updater(current));
  };

  const applyFilters = (
    nextFilters: LogFilterState,
    nextDates: [string, string] = dateValue,
  ) => {
    setFilters(nextFilters);
    setDateValue(nextDates);
    updatePaginateQuery((current) => ({
      ...current,
      page: 1,
      filter: filtersToQueryFilter(nextFilters, nextDates[0], nextDates[1]),
    }));
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    setGroupBy(readGroupBy(queryParams));
    const from = toLogDateTime(
      queryParams.get("filter[from]") || defaultRange[0],
      "start",
    );
    const to = toLogDateTime(
      queryParams.get("filter[to]") || defaultRange[1],
      "end",
    );
    const nextFilters: LogFilterState = {
      ...DEFAULT_FILTERS,
      ...readFiltersFromQuery(queryParams),
    };

    setFilters(nextFilters);
    setDateValue([from, to]);
    const query = new PaginateQuery(
      Number(queryParams.get("limit")) || 25,
      (queryParams.get("sortBy") as string) || "timestamp:DESC",
      filtersToQueryFilter(nextFilters, from, to),
    );
    query.page = Number(queryParams.get("page")) || 1;
    query.search = (queryParams.get("search") as string) || "";
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, filter } = paginateQuery;
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    if (limit) params.set("limit", String(limit));
    if (sortBy) params.set("sortBy", sortBy);
    if (search) params.set("search", search);
    if (groupBy !== "signature") params.set("groupBy", groupBy);
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key]) {
          params.set(`filter[${key}]`, String(filter[key]));
        }
      });
    }
    history.replace({
      search: params.toString() ? `?${params.toString()}` : "",
    });
  }, [paginateQuery, history, groupBy]);

  const activeFilterChips = useMemo(() => {
    const chips: { key: keyof LogFilterState; label: string }[] = [];
    if (filters.level)
      chips.push({ key: "level", label: `${t("level")}: ${filters.level}` });
    if (filters.serviceName)
      chips.push({
        key: "serviceName",
        label: `${t("serviceName")}: ${filters.serviceName}`,
      });
    if (filters.host)
      chips.push({ key: "host", label: `${t("host")}: ${filters.host}` });
    if (filters.method)
      chips.push({ key: "method", label: `${t("method")}: ${filters.method}` });
    if (filters.statusCode)
      chips.push({
        key: "statusCode",
        label: `${t("statusCode")}: ${filters.statusCode}`,
      });
    if (filters.errorCode)
      chips.push({
        key: "errorCode",
        label: `${t("errorCode")}: ${filters.errorCode}`,
      });
    if (filters.errorName)
      chips.push({
        key: "errorName",
        label: `${t("errorName")}: ${filters.errorName}`,
      });
    if (filters.userId)
      chips.push({ key: "userId", label: `${t("userId")}: ${filters.userId}` });
    if (filters.ip)
      chips.push({ key: "ip", label: `${t("ip")}: ${filters.ip}` });
    if (filters.message) {
      const preview =
        filters.message.length > 80
          ? `${filters.message.slice(0, 80)}…`
          : filters.message;
      chips.push({ key: "message", label: `${t("message")}: ${preview}` });
    }
    if (filters.ipMissing)
      chips.push({ key: "ipMissing", label: t("missingIp") });
    if (filters.httpErrorsOnly)
      chips.push({ key: "httpErrorsOnly", label: t("httpErrorsOnly") });
    return chips;
  }, [filters, t]);

  const columns: TableColumn<LogsDto>[] = [
    {
      name: <ColumnHeader title="date" />,
      cell: (row) => <DateColumn date={row.timestamp} />,
      sortable: true,
      width: "180px",
    },
    {
      name: <ColumnHeader title="level" />,
      cell: (row) => <LogLevelBadge level={row.level} />,
      sortable: true,
      width: "110px",
    },
    {
      name: <ColumnHeader title="serviceName" />,
      cell: (row) => (
        <TextColumn
          textCapitalize={false}
          isNeedToTranslate={false}
          text={row.serviceName || "-"}
        />
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: <ColumnHeader title="environment" />,
      cell: (row) => (
        <Badge color="light" className="text-dark">
          {row.environment || "-"}
        </Badge>
      ),
      sortable: false,
      width: "120px",
    },
    {
      name: <ColumnHeader title="message" />,
      cell: (row) => (
        <span className="logs-message-cell" title={row.message || ""}>
          {row.message || "-"}
        </span>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="path" />,
      cell: (row) => (
        <span className="logs-path-cell" title={row.path || ""}>
          {row.path || "-"}
        </span>
      ),
      sortable: false,
      width: "180px",
    },
    {
      name: <ColumnHeader title="statusCode" />,
      cell: (row) => (
        <TextColumn isNeedToTranslate={false} text={row.statusCode || "-"} />
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: <ColumnHeader title="ip" />,
      cell: (row) => (
        <TextColumn
          textCapitalize={false}
          isNeedToTranslate={false}
          text={row.ip || "-"}
        />
      ),
      sortable: false,
      width: "130px",
    },
    {
      name: <ColumnHeader title="action" />,
      cell: (row) => (
        <Button
          className="btn btn-success btn-sm"
          onClick={() => setSelectedLog(row)}
        >
          {t("viewDetail")}
        </Button>
      ),
      sortable: false,
      width: "130px",
    },
  ];

  const onSearch = (searchText: string) => {
    updatePaginateQuery((current) => ({
      ...current,
      page: 1,
      search: searchText,
    }));
  };

  const onChangeFilter = (
    key: keyof LogFilterState,
    value: string | boolean,
  ) => {
    applyFilters({ ...filters, [key]: value });
  };

  const onClearFilters = () => {
    applyFilters(DEFAULT_FILTERS, dateValue);
  };

  const onRemoveChip = (key: keyof LogFilterState) => {
    if (key === "httpErrorsOnly" || key === "ipMissing") {
      applyFilters({ ...filters, [key]: false });
      return;
    }
    applyFilters({ ...filters, [key]: "" });
  };

  const onChangeDateBound = (bound: "start" | "end", date: Date) => {
    const selected = moment(date);
    if (!selected.isValid()) {
      return;
    }

    const currentFrom = moment(dateValue[0]);
    const currentTo = moment(dateValue[1]);
    const nextFrom = bound === "start" ? selected : currentFrom;
    const nextTo = bound === "end" ? selected : currentTo;
    const from = nextFrom.isAfter(nextTo) ? nextTo.clone() : nextFrom.clone();
    const to = nextFrom.isAfter(nextTo) ? nextFrom.clone() : nextTo.clone();

    applyFilters(filters, [
      formatLogDateTime(from.clone().millisecond(0)),
      formatLogDateTime(to.clone().millisecond(0)),
    ]);
  };

  const applyQuickRange = (
    range: "today" | "yesterday" | "last7" | "last30",
  ) => {
    let start = moment();
    let end = moment();

    if (range === "yesterday") {
      start = moment().subtract(1, "day");
      end = moment().subtract(1, "day");
    } else if (range === "last7") {
      start = moment().subtract(6, "day").startOf("day");
    } else if (range === "last30") {
      start = moment().subtract(29, "day").startOf("day");
    }

    applyFilters(filters, [startOfDay(start), endOfDay(end)]);
  };

  const onSort = (column: TableColumn<LogsDto>, sortDirection: string) => {
    const sortMap: Record<number, string> = {
      1: "timestamp",
      2: "level",
      3: "service_name",
      6: "status_code",
    };
    const sortBy = sortMap[column.id as number] || "timestamp";
    updatePaginateQuery((current) => ({
      ...current,
      sortBy: `${sortBy}:${sortDirection.toUpperCase()}`,
    }));
  };

  const onSelectServiceLevel = (serviceName: string, level: string) => {
    applyFilters({ ...DEFAULT_FILTERS, serviceName, level });
    setActiveTab("logs");
  };

  const onSelectGroup = (group: LogGroupDto) => {
    const nextFilters: LogFilterState = { ...DEFAULT_FILTERS };
    let search = "";

    if (groupBy === "ip") {
      if (group.ip) {
        nextFilters.ip = group.ip;
      } else {
        nextFilters.ipMissing = true;
      }
    } else if (groupBy === "message") {
      nextFilters.serviceName = group.serviceName;
      nextFilters.level = group.level;
      nextFilters.message = group.sampleMessage || "";
    } else {
      nextFilters.serviceName = group.serviceName;
      nextFilters.level = group.level;
      nextFilters.errorName = group.errorName || "";
      search = group.errorName ? "" : group.sampleMessage.slice(0, 60);
    }

    setFilters(nextFilters);
    updatePaginateQuery((current) => ({
      ...current,
      page: 1,
      search,
      filter: filtersToQueryFilter(nextFilters, dateValue[0], dateValue[1]),
    }));
    setActiveTab("logs");
  };

  return (
    <div className="page-content logs-page">
      <Container fluid>
        <BreadCrumb title={t("logs")} pageTitle={t("logs")} />

        <Card className="mb-3">
          <CardBody>
            <div className="logs-toolbar">
              <div>
                <h5 className="mb-1">{t("logsOverview")}</h5>
                <p className="text-muted mb-0 small">{t("logsOverviewHint")}</p>
              </div>
              <div className="logs-quick-ranges">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => applyQuickRange("today")}
                >
                  {t("today")}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => applyQuickRange("yesterday")}
                >
                  {t("yesterday")}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => applyQuickRange("last7")}
                >
                  {t("last7Days")}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => applyQuickRange("last30")}
                >
                  {t("last30Days")}
                </Button>
              </div>
            </div>

            <Row className="g-3 align-items-end">
              <Col md={6} lg={3}>
                <label className="form-label mb-1">{t("dateTimeFrom")}</label>
                <Flatpickr
                  className="form-control"
                  value={moment(dateValue[0]).toDate()}
                  options={{
                    enableTime: true,
                    enableSeconds: true,
                    time_24hr: true,
                    dateFormat: "Y-m-d H:i:S",
                  }}
                  onClose={(dates) => {
                    if (dates[0]) {
                      onChangeDateBound("start", dates[0]);
                    }
                  }}
                />
              </Col>
              <Col md={6} lg={3}>
                <label className="form-label mb-1">{t("dateTimeTo")}</label>
                <Flatpickr
                  className="form-control"
                  value={moment(dateValue[1]).toDate()}
                  options={{
                    enableTime: true,
                    enableSeconds: true,
                    time_24hr: true,
                    dateFormat: "Y-m-d H:i:S",
                  }}
                  onClose={(dates) => {
                    if (dates[0]) {
                      onChangeDateBound("end", dates[0]);
                    }
                  }}
                />
              </Col>
              <Col md={6} lg={3}>
                <label className="form-label mb-1">
                  {t("searchPlaceholder")}
                </label>
                <div className="search-box">
                  <DebounceInput
                    className="form-control"
                    placeholder={`${t("searchPlaceholder")}...`}
                    minLength={1}
                    debounceTimeout={300}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onSearch(event.target.value)
                    }
                    value={paginateQuery.search || ""}
                  />
                  <i className="ri-search-line search-icon" />
                </div>
              </Col>
              <Col
                md={6}
                lg={3}
                className="d-flex gap-2 justify-content-lg-end"
              >
                <Button color="success" onClick={() => refetch()}>
                  {t("refresh")}
                </Button>
                <Button color="light" onClick={onClearFilters}>
                  {t("clearFilters")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <LogStatsWidgets
          stats={stats}
          isLoading={statsLoading}
          activeLevel={filters.level}
          httpErrorsOnly={filters.httpErrorsOnly}
          onSelectTotal={() => {
            onClearFilters();
            setActiveTab("logs");
          }}
          onSelectLevel={(level) => {
            applyFilters({ ...DEFAULT_FILTERS, level });
            setActiveTab("logs");
          }}
          onSelectHttpErrors={() => {
            applyFilters({ ...DEFAULT_FILTERS, httpErrorsOnly: true });
            setActiveTab("logs");
          }}
        />

        <Nav tabs className="logs-tabs mt-3 mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "overview" })}
              onClick={() => setActiveTab("overview")}
              role="button"
            >
              <i className="ri-dashboard-2-line align-bottom me-1" />
              {t("overview")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "issues" })}
              onClick={() => setActiveTab("issues")}
              role="button"
            >
              <i className="ri-stack-line align-bottom me-1" />
              {t("groupedIssues")}
              {!!groups?.meta?.totalItems && (
                <Badge color="danger" pill className="ms-2">
                  {groups.meta.totalItems}
                </Badge>
              )}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "logs" })}
              onClick={() => setActiveTab("logs")}
              role="button"
            >
              <i className="ri-list-check align-bottom me-1" />
              {t("allLogs")}
            </NavLink>
          </NavItem>
        </Nav>

        {activeFilterChips.length > 0 && (
          <div className="logs-active-filters">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className="logs-filter-chip"
                onClick={() => onRemoveChip(chip.key)}
              >
                <span>{chip.label}</span>
                <i className="ri-close-line" />
              </button>
            ))}
          </div>
        )}

        <TabContent activeTab={activeTab}>
          <TabPane tabId="overview">
            <LogServiceHealth
              services={stats?.byService}
              isLoading={statsLoading}
              activeService={filters.serviceName}
              onSelectService={(serviceName) => {
                applyFilters({ ...DEFAULT_FILTERS, serviceName });
                setActiveTab("logs");
              }}
              onSelectServiceLevel={onSelectServiceLevel}
            />
            <div className="mt-3">
              <LogGroupsPanel
                groups={groups?.data?.slice(0, 8)}
                isLoading={groupsLoading}
                title={t("topRecurringIssues")}
                groupBy={groupBy}
                onChangeGroupBy={setGroupBy}
                onSelectGroup={onSelectGroup}
              />
            </div>
          </TabPane>

          <TabPane tabId="issues">
            <LogGroupsPanel
              groups={groups?.data}
              isLoading={groupsLoading}
              groupBy={groupBy}
              onChangeGroupBy={setGroupBy}
              onSelectGroup={onSelectGroup}
            />
          </TabPane>

          <TabPane tabId="logs">
            <LogAdvancedFilters
              isOpen={advancedOpen}
              filters={filters}
              options={{
                services: stats?.services || [],
                hosts: stats?.hosts || [],
              }}
              onToggle={() => setAdvancedOpen((open) => !open)}
              onChange={onChangeFilter}
              onClear={onClearFilters}
            />

            <Card>
              <CardBody>
                <Row className="mb-3">
                  <Col className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">{t("logs")}</h6>
                      <p className="text-muted mb-0 small">
                        {t("showingResults", {
                          count: response?.meta?.totalItems ?? 0,
                        })}
                      </p>
                    </div>
                  </Col>
                </Row>
                <AppTable
                  theme="dark"
                  columns={columns}
                  data={response?.data || []}
                  progressPending={loading}
                  progressComponent={<TableSpinner />}
                  pagination
                  paginationServer
                  sortServer
                  onSort={onSort}
                  onChangeRowsPerPage={(limit: number, page: number) =>
                    updatePaginateQuery((current) => ({
                      ...current,
                      limit,
                      page,
                    }))
                  }
                  onChangePage={(page: number) =>
                    updatePaginateQuery((current) => ({ ...current, page }))
                  }
                  defaultSortFieldId={1}
                  defaultSortAsc={false}
                  persistTableHead
                  paginationTotalRows={response?.meta?.totalItems}
                  paginationComponentOptions={{
                    rowsPerPageText: t("rowsPerPage"),
                    rangeSeparatorText: t("of"),
                  }}
                  fixedHeader
                  fixedHeaderScrollHeight="58vh"
                />
              </CardBody>
            </Card>
          </TabPane>
        </TabContent>
      </Container>

      <LogDetailModal
        show={!!selectedLog}
        onCloseClick={() => setSelectedLog(null)}
        log={selectedLog}
      />
    </div>
  );
};
